import { useMemo } from "react";

import { useAppSelector } from "@/redux/hooks";

import { useResolveOrganizationNormalizedNameQuery } from "@/pages/Private/redux/organization/organization.api";
import { normalizedCompanyNamesSelector } from "@/pages/Private/redux/organization/organization.slice";

import { ListPerson } from "../../../schema/list";
import { mentionsNormalizedCompanyName } from "./normalizedCompanyName";

/**
 * Gives the preview the same normalized company name the send will produce (PREC-3923).
 *
 * `organization.nameNormalized` is filled lazily by the first send that uses the variable, so a lead
 * nobody has messaged yet has nothing but the raw company name to show. This resolves it up front
 * and returns the lead with the value merged in, so every preview consumer downstream
 * (`replacePlaceholders`, `resolveDraftPreviewHtml`) keeps working unchanged.
 *
 * Two gates keep it from costing anything it should not: the templates must actually mention the
 * variable, and the organization must not already have the value. What is left is one request per
 * organization — RTK Query dedupes the four node cards previewing the same lead into a single call,
 * and the API caches the answer on the shared organization row, so it happens once ever.
 */
export const useNormalizedCompanyName = (
	lead: ListPerson,
	templates: (string | undefined)[]
): ListPerson => {
	const organization = lead?.prospect?.organization;
	const organizationId = organization?.id;
	const resolvedNames = useAppSelector(normalizedCompanyNamesSelector);

	const shouldResolve =
		!!organizationId && !organization?.nameNormalized && mentionsNormalizedCompanyName(templates);

	useResolveOrganizationNormalizedNameQuery(organizationId as number, { skip: !shouldResolve });

	const resolved = organizationId ? resolvedNames[organizationId] : undefined;

	return useMemo(() => {
		// The lead is typed non-optional to match the preview helpers, but the inbox can genuinely be
		// empty on first paint — hand it straight back rather than building an object around nothing.
		if (!lead || !resolved || organization?.nameNormalized) {
			return lead;
		}

		return {
			...lead,
			prospect: {
				...lead.prospect,
				organization: { ...organization, nameNormalized: resolved },
			},
		} as ListPerson;
	}, [lead, resolved, organization?.nameNormalized]);
};
